import type { ReactNode } from "react";
import { Navigate, Route, Routes, useLocation, useSearchParams } from "react-router-dom";
import { useSession } from "@/session/SessionProvider";
import { ConfirmProvider } from "@/components/feedback/Confirm";
import { ToastProvider } from "@/components/feedback/Toast";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import Landing from "@/routes/landing";
import Login from "@/routes/login";
import Register from "@/routes/register";
import AccessPage from "@/routes/access";
import AcceptInvite from "@/routes/accept-invite";
import Account from "@/routes/account";
import InvitesPage from "@/routes/invites";
import Impressum from "@/routes/impressum";
import Datenschutz from "@/routes/datenschutz";
import Dashboard from "@/routes/dashboard";
import MapEditor from "@/routes/map-editor";
import MapViewerPage from "@/routes/map-view";

function RequireAuth({ children }: { children: ReactNode }) {
	const { isAuthed, isLoading } = useSession();
	const location = useLocation();
	if (isLoading) {
		return (
			<div className="flex min-h-svh items-center justify-center text-sm text-fg-muted">
				Laden…
			</div>
		);
	}
	if (!isAuthed) {
		return <Navigate to="/login" replace state={{ from: location }} />;
	}
	return <>{children}</>;
}

function RequireAuthOrShareToken({ children }: { children: ReactNode }) {
	const { isAuthed, isLoading } = useSession();
	const location = useLocation();
	const [params] = useSearchParams();
	const hasToken = Boolean(params.get("t")?.trim());

	if (isLoading) {
		return (
			<div className="flex min-h-svh items-center justify-center text-sm text-fg-muted">
				Laden…
			</div>
		);
	}
	if (!isAuthed && !hasToken) {
		return <Navigate to="/login" replace state={{ from: location }} />;
	}
	return <>{children}</>;
}

export default function App() {
	return (
		<ToastProvider>
			<ConfirmProvider>
				<Routes>
					<Route path="/" element={<Landing />} />
					<Route path="/login" element={<Login />} />
					<Route path="/register" element={<Register />} />
					<Route path="/access" element={<AccessPage />} />
					<Route path="/accept/:code" element={<AcceptInvite />} />
					<Route path="/impressum" element={<Impressum />} />
					<Route path="/datenschutz" element={<Datenschutz />} />
					<Route path="/k/:mapId/view" element={<MapViewerPage />} />
					<Route
						element={
							<RequireAuth>
								<DashboardLayout />
							</RequireAuth>
						}
					>
						<Route path="/app" element={<Dashboard />} />
						<Route path="/account" element={<Account />} />
						<Route path="/invites" element={<InvitesPage />} />
					</Route>
					<Route
						path="/k/:mapId"
						element={
							<RequireAuthOrShareToken>
								<MapEditor />
							</RequireAuthOrShareToken>
						}
					/>
					<Route path="*" element={<Navigate to="/" replace />} />
				</Routes>
			</ConfirmProvider>
		</ToastProvider>
	);
}
